/**
 * CurrencyInput - Form-friendly currency input with formatting
 *
 * Features:
 * - Formats as currency on blur ($1,234.56)
 * - Allows raw number entry during focus
 * - Validation integration with error display
 * - Handles null/undefined values
 */

import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface CurrencyInputProps {
  /** Current value (in dollars, not cents) */
  value: number | null | undefined;
  /** Change handler */
  onChange: (value: number | null) => void;
  /** Error message */
  error?: string;
  /** Label text */
  label?: string;
  /** Whether field is required */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Input ID */
  id?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Minimum value */
  min?: number;
  /** Maximum value */
  max?: number;
  /** Allow negative values */
  allowNegative?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Additional className for the input */
  inputClassName?: string;
  /** Show inline (without label wrapper) */
  inline?: boolean;
  /** Size variant */
  size?: 'sm' | 'default' | 'lg';
}

/**
 * Format a number as currency string
 */
function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Parse currency string to number
 */
function parseCurrency(input: string): number | null {
  if (!input || input.trim() === '') {
    return null;
  }
  // Remove $ and commas
  const cleaned = input.replace(/[$,]/g, '').trim();
  if (cleaned === '' || cleaned === '-') {
    return null;
  }
  const num = Number.parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

/**
 * CurrencyInput - Form-friendly currency input
 */
export const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value,
  onChange,
  error,
  label,
  required = false,
  placeholder = '0.00',
  id,
  disabled = false,
  min,
  max,
  allowNegative = false,
  className,
  inputClassName,
  inline = false,
  size = 'default',
}) => {
  const inputId = id || React.useId();
  const [isFocused, setIsFocused] = React.useState(false);
  const [displayValue, setDisplayValue] = React.useState(() => formatCurrency(value));

  // Update display value when external value changes (and not focused)
  React.useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatCurrency(value));
    }
  }, [value, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    // Show raw number on focus for easier editing
    if (value !== null && value !== undefined) {
      setDisplayValue(value.toString());
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const parsed = parseCurrency(displayValue);

    // Validate and clamp
    let finalValue = parsed;
    if (finalValue !== null) {
      if (!allowNegative && finalValue < 0) {
        finalValue = 0;
      }
      if (min !== undefined && finalValue < min) {
        finalValue = min;
      }
      if (max !== undefined && finalValue > max) {
        finalValue = max;
      }
    }

    onChange(finalValue);
    setDisplayValue(formatCurrency(finalValue));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow only numbers, decimals, minus, and empty
    if (inputValue === '' || /^-?\d*\.?\d*$/.test(inputValue)) {
      setDisplayValue(inputValue);
    }
  };

  const sizeClasses = {
    sm: 'h-8 text-sm',
    default: 'h-10',
    lg: 'h-12 text-lg',
  };

  const inputElement = (
    <div className="relative w-full">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none text-sm">
        $
      </span>
      <Input
        id={inputId}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={!!error}
        aria-describedby={error ? `${inputId}-error` : undefined}
        className={cn(
          'pl-5 pr-2 text-right w-full',
          sizeClasses[size],
          error && 'border-destructive focus:border-destructive focus:ring-destructive',
          inputClassName,
        )}
      />
    </div>
  );

  if (inline) {
    return (
      <div className={className}>
        {inputElement}
        {error && (
          <p id={`${inputId}-error`} className="text-xs text-destructive mt-1">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <Label htmlFor={inputId}>
          {label} {required && <span className="text-destructive">*</span>}
        </Label>
      )}
      {inputElement}
      {error && (
        <p id={`${inputId}-error`} className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
};

CurrencyInput.displayName = 'CurrencyInput';

/**
 * Validate a currency value
 */
export function validateCurrencyValue(
  value: number | null | undefined,
  options: {
    required?: boolean;
    min?: number;
    max?: number;
    fieldName?: string;
  } = {},
): string | undefined {
  const { required = false, min, max, fieldName = 'Value' } = options;

  if (value === null || value === undefined) {
    if (required) {
      return `${fieldName} is required`;
    }
    return undefined;
  }

  if (isNaN(value)) {
    return `${fieldName} must be a valid number`;
  }

  if (min !== undefined && value < min) {
    return `${fieldName} must be at least $${min.toLocaleString()}`;
  }

  if (max !== undefined && value > max) {
    return `${fieldName} cannot exceed $${max.toLocaleString()}`;
  }

  return undefined;
}

export { formatCurrency, parseCurrency };
